import { Link } from "@tanstack/react-router";
import { Loader2, Mail, MessageCircle } from "lucide-react";
import { useId, useState } from "react";
import { DashboardPageHeader } from "@/components/dashboard/DashboardPageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SUPPORT_EMAIL } from "@/lib/constants";

const WHATSAPP_COMMUNITY_URL =
	"https://chat.whatsapp.com/FRn4t6QZUVqI9Hctw423Um?mode=gi_t";

interface SupportPageContentProps {
	defaultName?: string;
	defaultEmail?: string;
	showHeader?: boolean;
}

type FormStatus =
	| { kind: "idle" }
	| { kind: "submitting" }
	| { kind: "success" }
	| { kind: "error"; message: string };

export function SupportPageContent({
	defaultName = "",
	defaultEmail = "",
	showHeader = true,
}: SupportPageContentProps) {
	const [name, setName] = useState(defaultName);
	const [email, setEmail] = useState(defaultEmail);
	const [subject, setSubject] = useState("");
	const [message, setMessage] = useState("");
	const [website, setWebsite] = useState("");
	const [status, setStatus] = useState<FormStatus>({ kind: "idle" });

	const nameId = useId();
	const emailId = useId();
	const subjectId = useId();
	const messageId = useId();

	const isSubmitting = status.kind === "submitting";

	async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setStatus({ kind: "submitting" });

		try {
			const response = await fetch("/api/support/contact", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					name,
					email,
					subject,
					message,
					website,
				}),
			});

			const data = (await response.json().catch(() => ({
				success: false,
				error: "Unexpected server response",
			}))) as {
				success?: boolean;
				error?: string;
			};

			if (!response.ok || !data.success) {
				setStatus({
					kind: "error",
					message:
						data.error ||
						(response.status === 429
							? "Too many requests. Please try again in a bit."
							: "Something went wrong. Please try again."),
				});
				return;
			}

			setStatus({ kind: "success" });
			setSubject("");
			setMessage("");
		} catch (err) {
			console.error("[Support] Contact submit failed:", err);
			setStatus({
				kind: "error",
				message: "Network error. Please try again.",
			});
		}
	}

	return (
		<div className="space-y-10">
			{showHeader ? (
				<DashboardPageHeader
					title="Get support"
					description={
						<>
							Pick a channel below, or check the{" "}
							<Link
								to="/docs"
								className="text-primary hover:text-primary/80 transition-colors"
							>
								help docs
							</Link>{" "}
							first.
						</>
					}
				/>
			) : null}

			<div className="grid gap-4 sm:grid-cols-2">
				<a
					href={`mailto:${SUPPORT_EMAIL}`}
					className="group flex flex-col gap-3 rounded-md border border-border bg-background p-5 transition-colors hover:border-foreground/30"
				>
					<div className="flex size-9 items-center justify-center rounded-md border border-border bg-muted/40 text-muted-foreground">
						<Mail className="size-4" />
					</div>
					<div className="space-y-1">
						<p className="text-sm font-medium text-foreground">Email us</p>
						<p className="text-sm text-muted-foreground">{SUPPORT_EMAIL}</p>
						<p className="text-xs text-muted-foreground/80">
							Replies within 24 hours on weekdays.
						</p>
					</div>
				</a>

				<a
					href={WHATSAPP_COMMUNITY_URL}
					target="_blank"
					rel="noopener noreferrer"
					className="group flex flex-col gap-3 rounded-md border border-border bg-background p-5 transition-colors hover:border-foreground/30"
				>
					<div className="flex size-9 items-center justify-center rounded-md border border-border bg-muted/40 text-muted-foreground">
						<MessageCircle className="size-4" />
					</div>
					<div className="space-y-1">
						<p className="text-sm font-medium text-foreground">
							WhatsApp Community
						</p>
						<p className="text-sm text-muted-foreground">
							Chat with other users and the team.
						</p>
						<p className="text-xs text-muted-foreground/80">
							Best for quick questions and feedback.
						</p>
					</div>
				</a>
			</div>

			<div className="space-y-5">
				<div className="space-y-1.5">
					<h2 className="text-lg font-semibold tracking-tight text-foreground">
						Or send us a message
					</h2>
					<p className="text-sm text-muted-foreground">
						We'll reply to the email address you provide.
					</p>
				</div>

				<form onSubmit={handleSubmit} className="space-y-4" noValidate>
					<input
						type="text"
						name="website"
						value={website}
						onChange={(e) => setWebsite(e.target.value)}
						tabIndex={-1}
						autoComplete="off"
						aria-hidden="true"
						className="absolute left-[-9999px] h-0 w-0 opacity-0"
					/>

					<div className="grid gap-4 sm:grid-cols-2">
						<div className="space-y-1.5">
							<label
								htmlFor={nameId}
								className="text-sm font-medium text-foreground"
							>
								Name
							</label>
							<Input
								id={nameId}
								type="text"
								required
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="Your name"
								disabled={isSubmitting}
								autoComplete="name"
							/>
						</div>
						<div className="space-y-1.5">
							<label
								htmlFor={emailId}
								className="text-sm font-medium text-foreground"
							>
								Email
							</label>
							<Input
								id={emailId}
								type="email"
								required
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="you@example.com"
								disabled={isSubmitting}
								autoComplete="email"
							/>
						</div>
					</div>

					<div className="space-y-1.5">
						<label
							htmlFor={subjectId}
							className="text-sm font-medium text-foreground"
						>
							Subject{" "}
							<span className="text-xs font-normal text-muted-foreground">
								(optional)
							</span>
						</label>
						<Input
							id={subjectId}
							type="text"
							value={subject}
							onChange={(e) => setSubject(e.target.value)}
							placeholder="What's this about?"
							disabled={isSubmitting}
						/>
					</div>

					<div className="space-y-1.5">
						<label
							htmlFor={messageId}
							className="text-sm font-medium text-foreground"
						>
							Message
						</label>
						<Textarea
							id={messageId}
							required
							rows={6}
							minLength={10}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							placeholder="Tell us what's going on. The more detail, the better."
							disabled={isSubmitting}
						/>
					</div>

					{status.kind === "error" && (
						<div
							role="alert"
							className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
						>
							{status.message}
						</div>
					)}

					{status.kind === "success" && (
						<output className="block rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
							Thanks, we got your message. We'll reply within 24 hours.
						</output>
					)}

					<div className="flex items-center justify-end gap-3">
						<Button type="submit" disabled={isSubmitting}>
							{isSubmitting ? (
								<>
									<Loader2 className="size-4 animate-spin" />
									Sending
								</>
							) : (
								"Send message"
							)}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}
